<template>
  <div class="console-header">
    <!-- 深色顶栏背景条（56px 不变） -->
    <div class="console-top-bar"></div>
    <!-- 标签栏贴在下边缘 -->
    <div class="console-tabs-wrapper">
      <div class="console-tabs-scroll" ref="tabsScroll" role="tablist">
        <div
          v-for="tab in editableTabs"
          :key="tab.name"
          class="console-tab-item"
          :class="{ active: tab.name === editableTabsValue }"
          role="tab"
          :aria-selected="tab.name === editableTabsValue"
          :aria-label="tab.title"
          tabindex="0"
          @click="switchToTab(tab)"
          @keydown.enter="switchToTab(tab)"
        >
          <span class="console-tab-label">{{ tab.title }}</span>
          <span
            class="console-tab-close"
            v-if="editableTabs.length > 1"
            @click.stop="removeTab(tab.name)"
            role="button"
            tabindex="0"
            @keydown.enter.stop="removeTab(tab.name)"
            aria-label="关闭标签"
          >&times;</span>
        </div>
      </div>
      <!-- 关闭全部按钮 — 固定在右侧，层级高于滚动区 -->
      <div
        class="console-tabs-close-all"
        v-if="editableTabs.length > 1"
        @click="closeAllTabs"
        title="关闭全部标签"
        role="button"
        tabindex="0"
        @keydown.enter="closeAllTabs"
        aria-label="关闭全部标签"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          <circle cx="12" cy="12" r="10" stroke-opacity="0.4"/>
        </svg>
      </div>
    </div>
  </div>
</template>

<script>
import Bus from '@/utils/bus.js'

// ponytail: 从 ConsoleSideMenu 复制的菜单树，仅用于 titleForPath 查中文名
var MENU_TREE = [
  { group: '监测管理', groupKey: 'SITES', children: [
    { name: '诊断详情', path: '/console/sites/list' },
    { name: '数据详情', path: '/console/sites/data' },
    { name: '评估报告', path: '/console/sites/reports' },
    { name: '报告详情', path: '/console/sites/report' },
  ]},
  { group: '告警记录', groupKey: 'ALERTS', children: [
    { name: '用电告警', path: '/console/alerts/electrical' },
    { name: '环境告警', path: '/console/alerts/environment' },
    { name: '隐患预警', path: '/console/alerts/hazard' },
    { name: '隐患设备', path: '/console/alerts/equipment' },
  ]},
  { group: '终端设备', groupKey: 'DEVICES', children: [
    { name: '设备管理', path: '/console/devices/manage' },
    { name: '设备运维', path: '/console/devices/ops' },
  ]},
  { group: '能耗分析', groupKey: 'ENERGY', children: [
    { name: '能耗总览', path: '/console/energy/overview' },
    { name: '用能统计', path: '/console/energy/statistics' },
    { name: '碳管理', path: '/console/energy/carbon' },
    { name: '电能质量', path: '/console/energy/quality' },
    { name: '用电集抄', path: '/console/energy/meter' },
  ]},
  { group: '系统设置', groupKey: 'SETTINGS', sections: [
    { title: '账号管理', children: [
      { name: '用户管理', path: '/console/settings/users' },
      { name: '角色管理', path: '/console/settings/roles' },
      { name: '组织管理', path: '/console/settings/orgs' },
      { name: '合作方管理', path: '/console/settings/partners' },
      { name: '应用管理', path: '/console/settings/apps' },
      { name: '标签管理', path: '/console/settings/tags' },
    ]},
    { title: '告警配置', children: [
      { name: '隐患类型配置', path: '/console/settings/alert-types' },
      { name: '推送配置', path: '/console/settings/push' },
      { name: '短信推送', path: '/console/settings/sms' },
      { name: '语音推送', path: '/console/settings/voice' },
      { name: '组织隐患配置', path: '/console/settings/hazard-config' },
    ]},
    { title: '审计日志', children: [
      { name: '操作日志', path: '/console/settings/logs' },
    ]},
  ]},
  { group: '账号中心', groupKey: 'PROFILE', children: [
    { name: '基本信息', path: '/console/profile' },
    { name: '访问密钥', path: '/console/access-key' },
  ]},
]

const titleForPath = (path) => {
  // ponytail: 先查 MENU_TREE 拿中文名，找不到再用路径最后一段
  var group = MENU_TREE.find(function (g) {
    if (g.children) return g.children.some(function (c) { return c.path === path })
    if (g.sections) return g.sections.some(function (s) { return s.children.some(function (c) { return c.path === path }) })
    return false
  })
  if (group) {
    var item
    if (group.children) item = group.children.find(function (c) { return c.path === path })
    if (!item && group.sections) {
      item = group.sections.reduce(function (found, s) {
        return found || s.children.find(function (c) { return c.path === path })
      }, null)
    }
    if (item) return item.name
  }
  var segments = path.replace(/\/+$/, '').split('/')
  return segments[segments.length - 1] || '首页'
}

export default {
  name: 'ConsoleHeader',
  data() {
    return {
      editableTabsValue: '',
      tabIndex: 0,
      editableTabs: [],
    }
  },
  methods: {
    // ponytail: 判断是否为"详情页"类标签（按 siteId 或报告编号区分实例）
    isDetailRoute(path) {
      return path.indexOf('/console/sites/detail') !== -1 || path.indexOf('/console/sites/report') !== -1
    },

    // ponytail: 生成详情页路由对象，报告按 row 查询，监测点按 siteId/branchId
    detailRoute(tab) {
      if (tab.router.indexOf('/console/sites/report') !== -1 && tab.row) {
        return { path: tab.router, query: { row: tab.row, from: 'report-list' } }
      }
      return { path: tab.router, query: { siteId: tab.id, branchId: tab.branchId || undefined } }
    },

    switchToTab(tab) {
      this.editableTabsValue = tab.name
      localStorage.setItem('editableTabsValue', tab.name)
      sessionStorage.setItem('saveid', tab.id)
      if (this.isDetailRoute(tab.router) && tab.id) {
        this.$router.push(this.detailRoute(tab))
      } else {
        this.$router.push(tab.router)
      }
    },
    removeTab(targetName) {
      if (this.editableTabs.length <= 1) return
      var tabs = this.editableTabs
      var activeName = this.editableTabsValue
      var needPushRouter = false
      var router = ''
      if (activeName === targetName) {
        needPushRouter = true
        tabs.forEach(function (tab, index) {
          if (tab.name === targetName) {
            var nextTab = tabs[index + 1] || tabs[index - 1]
            if (nextTab) {
              activeName = nextTab.name
              router = nextTab.router
            }
          }
        })
      }
      this.editableTabs = tabs.filter(function (tab) { return tab.name !== targetName })
      this.editableTabsValue = activeName
      localStorage.setItem('editableTabs', JSON.stringify(this.editableTabs))
      localStorage.setItem('editableTabsValue', this.editableTabsValue)
      if (needPushRouter && router) {
        var currentTab = this.editableTabs.find(function (t) { return t.name === activeName })
        if (currentTab) {
          sessionStorage.setItem('saveid', currentTab.id)
        }
        // ponytail: 关闭标签后跳转也需携带 query
        if (currentTab && this.isDetailRoute(currentTab.router) && currentTab.id) {
          this.$router.push(this.detailRoute(currentTab))
        } else {
          this.$router.push(router)
        }
      }
    },
    // ponytail: 仅为左侧栏路由创建标签，忽略 detail 类路由（由 pushrouter 事件管理）
    ensureTabForPath(path) {
      if (!path.startsWith('/console/')) return
      // 左侧栏路由按 router path 去重（一个路由一个标签）
      var exists = this.editableTabs.some(function (t) { return t.router === path })
      if (exists) {
        var tab = this.editableTabs.find(function (t) { return t.router === path })
        this.editableTabsValue = tab.name
        localStorage.setItem('editableTabsValue', tab.name)
        return
      }
      var title = titleForPath(path)
      var newTabName = ++this.tabIndex + ''
      this.editableTabs.push({ title: title, name: newTabName, branchId: '', id: title, router: path })
      this.editableTabsValue = newTabName
      localStorage.setItem('editableTabs', JSON.stringify(this.editableTabs))
      localStorage.setItem('editableTabsValue', newTabName)
    },
    closeAllTabs() {
      this.editableTabs = []
      this.editableTabsValue = ''
      localStorage.removeItem('editableTabs')
      localStorage.removeItem('editableTabsValue')
      this.tabIndex = 0
      Bus.$emit('pushrouter', ['诊断详情', '', '', '/console/sites/list'])
    },
    scrollActiveTabIntoView() {
      this.$nextTick(function () {
        var scrollEl = this.$refs.tabsScroll
        if (!scrollEl) return
        var active = scrollEl.querySelector('.console-tab-item.active')
        if (active) {
          var containerRect = scrollEl.getBoundingClientRect()
          var activeRect = active.getBoundingClientRect()
          if (activeRect.right > containerRect.right) {
            scrollEl.scrollLeft += activeRect.right - containerRect.right + 12
          } else if (activeRect.left < containerRect.left) {
            scrollEl.scrollLeft -= containerRect.left - activeRect.left + 12
          }
        }
      })
    },
  },
  watch: {
    '$route.path': {
      handler: function (path) {
        // ponytail: detail 类路由由 pushrouter/pushrouter2 事件管理，不在此处创建标签
        if (this.isDetailRoute(path)) return
        this.ensureTabForPath(path)
        this.scrollActiveTabIntoView()
      },
    },
  },
  mounted() {
    var savedTabs = localStorage.getItem('editableTabs')
    var savedValue = localStorage.getItem('editableTabsValue')
    var tabsRestored = false
    if (savedTabs) {
      try {
        var parsed = JSON.parse(savedTabs)
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.editableTabs = parsed
          this.tabIndex = parsed.reduce(function (max, t) { return Math.max(max, parseInt(t.name, 10) || 0) }, 0)
          if (savedValue && parsed.some(function (t) { return t.name === savedValue })) {
            tabsRestored = true
            this.editableTabsValue = savedValue
            var activeTab = parsed.find(function (t) { return t.name === savedValue })
            if (activeTab && activeTab.id && activeTab.id !== 'list') {
              sessionStorage.setItem('saveid', activeTab.id)
              if (activeTab.branchId) {
                sessionStorage.setItem('branchId', activeTab.branchId)
              }
            }
          }
        }
      } catch (e) {}
    }

    this.$nextTick(function () {
      // ponytail: 已从 localStorage 恢复标签页时，优先匹配当前路由对应的标签
      // 若当前路由不在已恢复的标签中（如从看板直接跳转至控制台），则创建新标签而非重定向
      if (tabsRestored) {
        var activeTab = this.editableTabs.find(function (t) { return t.name === this.editableTabsValue }.bind(this))
        // ponytail: When current route doesn't exist in saved tabs (e.g. navigated from dashboard),
        // create a tab for it instead of redirecting to the saved active tab.
        var currentTab = this.editableTabs.find(function (t) { return t.router === this.$route.path }.bind(this))
        if (currentTab) {
          this.editableTabsValue = currentTab.name
          localStorage.setItem('editableTabsValue', currentTab.name)
        } else if (activeTab && activeTab.router && activeTab.router !== this.$route.path) {
          if (!this.isDetailRoute(this.$route.path)) {
            this.ensureTabForPath(this.$route.path)
          } else {
            this.$router.replace(
              (this.isDetailRoute(activeTab.router) && activeTab.id)
                ? this.detailRoute(activeTab)
                : activeTab.router
            )
          }
        }
        this.scrollActiveTabIntoView()
        return
      }
      if (!this.isDetailRoute(this.$route.path)) {
        this.ensureTabForPath(this.$route.path)
      }
      this.scrollActiveTabIntoView()
    })

    var that = this

    // ponytail: pushrouter/pushrouter2/switchTab 共用同一套 tab 查找/创建逻辑
    // suffix: 新标签名的后缀（'' 或 'other'），用于区分不同事件的标签流
    function handlePushRouter(val, suffix) {
      suffix = suffix || ''
      var isDetail = that.isDetailRoute(val[3])
      for (var i = 0; i < that.editableTabs.length; i++) {
        var match = isDetail
          ? (that.editableTabs[i].id === val[2] && that.editableTabs[i].router === val[3])
          : (that.editableTabs[i].title === val[0])
        if (match) {
          that.editableTabsValue = that.editableTabs[i].name
          localStorage.setItem('editableTabsValue', that.editableTabsValue)
          sessionStorage.setItem('saveid', that.editableTabs[i].id)
          that.$router.push(isDetail ? that.detailRoute(that.editableTabs[i]) : that.editableTabs[i].router)
          return
        }
      }
      var newTabName = ++that.tabIndex + suffix
      var newTab = { title: val[0], name: newTabName, branchId: val[1], id: val[2], router: val[3] }
      if (val.length > 4 && val[4]) newTab.row = val[4]
      that.editableTabs.push(newTab)
      that.editableTabsValue = newTabName
      localStorage.setItem('editableTabs', JSON.stringify(that.editableTabs))
      // ponytail: 等路由切换完成后才保存 editableTabsValue，避免切换空挡中刷新导致页签与内容错位
      that.$router.push(isDetail && val[2] ? that.detailRoute(newTab) : val[3])
        .catch(function () {})
        .then(function () {
          localStorage.setItem('editableTabsValue', newTabName)
        })
    }

    Bus.$on('pushrouter', function (val) { handlePushRouter(val, '') })
    Bus.$on('switchTab', function (tabName) {
      var tab = that.editableTabs.find(function (t) { return t.name === tabName })
      if (tab) {
        handlePushRouter([tab.title, tab.branchId, tab.id, tab.router], '')
      }
    })
    // ponytail: pushrouter2 用于 data detail 页签，suffix='other' 避免与 pushrouter 的 tabIndex 冲突
    Bus.$on('pushrouter2', function (val) { handlePushRouter(val, 'other') })
    // ponytail: 报告页打开后更新标签标题为报告名称，按 (router, id) 匹配支持多报告标签
    Bus.$on('updateTabTitle', function (path, title, id) {
      var tab = that.editableTabs.find(function (t) {
        return t.router === path && (!id || t.id === id)
      })
      if (tab) {
        tab.title = title
        localStorage.setItem('editableTabs', JSON.stringify(that.editableTabs))
      }
    })
  },
  beforeDestroy() {
    Bus.$off('pushrouter')
    Bus.$off('pushrouter2')
    Bus.$off('switchTab')
    Bus.$off('updateTabTitle')
  },
}
</script>

<style scoped>
:root {
  --console-tab-label-max-width: 220px;
}
.console-header {
  position: relative;
  flex-shrink: 0;
  z-index: 200;
}

/* 深色背景条 — 高度 56px 不变 */
.console-top-bar {
  width: 100%;
  height: 56px;
  min-height: 56px;
  background: #0A1628;
  border-bottom: 1px solid rgba(42, 98, 255, 0.15);
  position: relative;
}
.console-top-bar::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(90deg, rgba(42, 98, 255, 0.10) 0%, transparent 60%);
  pointer-events: none;
}

/* 标签栏 — 绝对定位贴在顶栏底部 */
.console-tabs-wrapper {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 36px;
  overflow: hidden;
  pointer-events: none;
}
.console-tabs-scroll {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0 12px;
  scrollbar-width: none;
  pointer-events: auto;
  /* 横向滚动丝滑 */
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}
.console-tabs-scroll::-webkit-scrollbar {
  display: none;
}

/* 单个标签 */
.console-tab-item {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 14px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-bottom: none;
  border-radius: 6px 6px 0 0;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  transition: all 0.2s ease;
  position: relative;
  margin-bottom: 0;
}
.console-tab-item:hover {
  color: rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.10);
}
.console-tab-item:focus-visible {
  outline: 2px solid #8AB4FF;
  outline-offset: -2px;
}
.console-tab-item.active {
  color: #1a1a2e;
  background: #fff;
  border-color: #e4e7ed;
  border-bottom-color: #fff;
  font-weight: 600;
}
.console-tab-item.active .console-tab-close {
  color: #999;
}
.console-tab-item.active .console-tab-close:hover {
  color: #e74c3c;
  background: rgba(231, 76, 60, 0.08);
}
/* 标签文字 — 过长时省略 */
.console-tab-label {
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: var(--console-tab-label-max-width, 220px);
}

/* 关闭按钮 */
.console-tab-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  font-size: 14px;
  line-height: 1;
  border-radius: 3px;
  color: rgba(255, 255, 255, 0.3);
  transition: all 0.15s;
  flex-shrink: 0;
}
.console-tab-close:hover {
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.12);
}

/* 关闭全部按钮 — 固定在右侧，视觉层级高于滚动标签 */
.console-tabs-close-all {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.35);
  background: #0A1628;
  border-left: 1px solid rgba(42, 98, 255, 0.15);
  transition: all 0.2s;
  flex-shrink: 0;
  z-index: 1;
  pointer-events: auto;
}
.console-tabs-close-all:hover {
  color: rgba(255, 255, 255, 0.85);
  background: rgba(231, 76, 60, 0.15);
  border-left-color: rgba(231, 76, 60, 0.25);
}
.console-tabs-close-all:focus-visible {
  outline: 2px solid #8AB4FF;
  outline-offset: -2px;
}
</style>
